/**
 * errorReporter — global error capture for non-React errors.
 *
 * AUTOMATIC TICKET FILING IS DISABLED. Nothing in this module writes to
 * support_tickets any more. The only reports the system accepts are the ones a
 * human files through "Report a problem or suggest an improvement"
 * (src/features/support/SupportModal.tsx → POST /github/issues). The autofix
 * agent is off entirely — see supabase/functions/error-autofix/index.ts and
 * migration 20260821120000_disable_error_autofix_and_auto_reporting.sql.
 *
 * What this module still does, because it helps the user rather than reporting
 * on them:
 *   • Stale deployment (chunk load failure) → silent reload, then a
 *     "New version available" toast
 *   • Filters extension / PDF / browser noise out of the console
 *
 * React's ErrorBoundary handles render-time crashes and offers the manual
 * report flow; it no longer auto-files either.
 */

import { toast } from 'sonner';
import {
  isBenignBrowserNoise,
  isBenignBrowserNoiseArgs,
  isBenignPdfNoise,
  isBenignPdfNoiseArgs,
  isExtensionNoise,
  isExtensionNoiseArgs,
} from './suppressCanvasErrors';

// ── Dev sessions never file tickets ──────────────────────────────────────────
// `npm run dev` errors are the developer's own in-progress work (Vite HMR
// failures, mid-edit syntax errors) and were the bulk of support_tickets
// noise. import.meta.env.DEV is the gate rather than a hostname check because
// the native shells serve BUILT bundles from tauri://localhost /
// capacitor://localhost - a hostname test would silence real production
// errors from the Mac and iOS apps.
export const IS_DEV_SESSION = import.meta.env.DEV;

// ── Dedup: don't show/report the same message more than once per session ──────
const _reported = new Set<string>();

function dedup(key: string): boolean {
  if (_reported.has(key)) return true;
  _reported.add(key);
  return false;
}

// ── Stale deployment detection ────────────────────────────────────────────────
// "Failed to fetch dynamically imported module" fires when a new build is
// deployed while the user has the old app open. Their browser requests a chunk
// that no longer exists (different content hash). Nothing to fix in the code.
const STALE_DEPLOY_PATTERNS = [
  'Failed to fetch dynamically imported module',
  'Importing a module script failed',      // Firefox/Safari variant
  'error loading dynamically imported module', // older Chrome
  'Unable to preload CSS',                 // Vite's stale-CSS-chunk variant
];

function isStaleDeploymentError(message: string): boolean {
  return STALE_DEPLOY_PATTERNS.some(p => message.includes(p));
}

/** Shared with ErrorBoundary: whichever side reloads first sets this, and the
 *  boundary only auto-reloads while it is unset - one automatic reload per
 *  stale-deploy incident, then the visible "new version" screen. */
export const STALE_RELOAD_FLAG = 'qs_stale_reload';

function handleStaleDeployment() {
  if (dedup('stale-deployment')) return;

  // A stale-chunk failure means a new build deployed under a running session —
  // the browser requested a content-hashed chunk that no longer exists. The fix
  // is a reload, so do it SILENTLY rather than nagging the user (this is the
  // native-feeling behaviour for a WebShell — the app just quietly updates).
  //
  // Loop guard: if we already auto-reloaded very recently and STILL hit a stale
  // chunk, the reload isn't resolving it (offline, a genuinely broken deploy, or
  // a chunk that 404s for another reason). Don't reload again — fall back to the
  // visible "Refresh now" toast so we never spin the page in a reload loop.
  const KEY = 'qs:chunk-auto-reload-at';
  const RECENT_MS = 20_000;
  let lastReload = 0;
  try { lastReload = Number(sessionStorage.getItem(KEY) || '0'); } catch {}
  const now = Date.now();
  if (now - lastReload > RECENT_MS) {
    try { sessionStorage.setItem(KEY, String(now)); } catch {}
    // ErrorBoundary keeps its own one-shot auto-reload flag for stale-deploy
    // errors. Stamp it here so the reload this handler initiates counts as
    // that one shot too - otherwise a still-broken deploy gets a SECOND
    // boundary-driven reload before the "new version" screen appears.
    try { sessionStorage.setItem(STALE_RELOAD_FLAG, '1'); } catch {}
    window.location.reload();
    return;
  }

  toast('New version available', {
    description: 'Quantis has been updated. Refresh to load the latest version.',
    duration: Infinity, // stays until dismissed or they refresh
    action: {
      label: 'Refresh now',
      onClick: () => window.location.reload(),
    },
  });
}

// ── Third-party filter ────────────────────────────────────────────────────────
const THIRD_PARTY_PATTERNS = [
  'AdobeDCViewApp', 'dc-core.js', 'dc-mobx.js', 'ViewSDK.js',
  'renderingWorker', 'maps.googleapis.com', 'googletagmanager',
  'facebook.net', 'intercom', 'stripe.js', 'recaptcha',
];

// E2E key-publishing warnings are non-fatal and self-healing (retried on next load).
// E2E key-publishing warnings are non-fatal and self-healing (retried on next load).
const BENIGN_WARN_PATTERNS = [
  '[E2E] Failed to publish public key',
  'Failed to publish public key',
  '[TakeoffPreviewCanvas] scale detection failed',
  '[WorkflowTriggerService] Failed to fire workflow event',
];
// paths — report everything unless the first frame is purely third-party.
function isOurError(stack: string | undefined): boolean {
  if (!stack) return true;
  if (/\bsrc\//.test(stack)) return true;
  const firstFrame = stack.split('\n').find(line => line.trim().startsWith('at '));
  if (!firstFrame) return true;
  return !THIRD_PARTY_PATTERNS.some(p => firstFrame.includes(p));
}

// ── Open a bug ticket in support_tickets ────────────────────────────────────
export function isBenignWarn(message: string | undefined): boolean {
  if (!message) return false;
  return BENIGN_WARN_PATTERNS.some(p => message.includes(p));
}

function report(error: Error | null, source: string, extra?: { url?: string }) {
  if (IS_DEV_SESSION) return;
  if (!error) return;
  if (isBenignWarn(error.message)) return;
  if (
    isExtensionNoise(error.message) ||
    isExtensionNoise(error.stack) ||
    isExtensionNoise(extra?.url) ||
    isBenignPdfNoise(error.message) ||
    isBenignPdfNoise(error.stack) ||
    isBenignBrowserNoise(error.message) ||
    isBenignBrowserNoise(error.stack)
  ) {
    return;
  }

  // Stale deployment — handle with a toast, don't file a bug report
  if (isStaleDeploymentError(error.message)) {
    handleStaleDeployment();
    return;
  }

  if (!isOurError(error.stack)) return;

  // Deliberately no DB write. Automatic reporting is disabled — a report only
  // exists if a human filed it through the support modal. The dedup and the
  // classification above are kept so stale-deploy recovery and the noise
  // filters still work, and so re-enabling would be a local change here.
  dedup(`${error.name}:${error.message}`);
  void source;
}

// ── Install global listeners ──────────────────────────────────────────────────
let _installed = false;

export function installErrorReporter() {
  if (_installed || typeof window === 'undefined') return;
  _installed = true;

  // 0. Vite's canonical stale-chunk signal: fires when a dynamically-imported
  //    module fails to load because a new build deployed under this session.
  //    Recover silently (guarded reload).
  //
  //    Deliberately NOT cancelled. Vite's preload helper only rethrows when the
  //    event was not cancelled, so a preventDefault() here makes the failed
  //    import RESOLVE WITH `undefined` instead of rejecting — and every
  //    `lazy(() => import(x).then(m => ({ default: m.Y })))` site then throws a
  //    TypeError whose message dodges isStaleDeploymentError, so it is reported
  //    as a bug and rendered as a crash (ERR-197G6AN). Left uncancelled, the
  //    import rejects with the real stale-chunk message, which both the
  //    unhandledrejection handler below and ErrorBoundary already classify and
  //    self-heal from. Do not re-add preventDefault().
  window.addEventListener('vite:preloadError', () => {
    handleStaleDeployment();
  });

  // 1. Synchronous errors thrown outside React
  window.addEventListener('error', (event: ErrorEvent) => {
    if (
      isExtensionNoise(event.message) ||
      isExtensionNoise(event.filename) ||
      isBenignPdfNoise(event.message) ||
      isBenignBrowserNoise(event.message)
    ) {
      return;
    }
    report(event.error ?? new Error(event.message), 'window.onerror', {
      url: event.filename || window.location.href,
    });
  });

  // 2. Unhandled promise rejections — this is where chunk load failures land
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    const error = reason instanceof Error
      ? reason
      : new Error(typeof reason === 'string' ? reason : JSON.stringify(reason));
    if (
      isExtensionNoise(error.message) ||
      isExtensionNoise(error.stack) ||
      isBenignPdfNoise(error.message) ||
      isBenignPdfNoise(error.stack) ||
      isBenignBrowserNoise(error.message) ||
      isBenignBrowserNoise(error.stack)
    ) {
      return;
    }
    report(error, 'unhandledrejection');
  });

  // 3. console.error — report Error objects AND plain string messages from our code
  const originalError = console.error;
  console.error = (...args: unknown[]) => {
    if (
      isExtensionNoiseArgs(args) ||
      isBenignPdfNoiseArgs(args) ||
      isBenignBrowserNoiseArgs(args)
    ) {
      originalError.apply(console, args);
      return;
    }
    const err = args.find((a): a is Error => a instanceof Error);
    if (err) {
      report(err, 'console.error');
    } else {
      // Plain string call — wrap so it reaches support_tickets
      const msg = args
        .map(a => (typeof a === 'string' ? a : a instanceof Object ? JSON.stringify(a) : String(a)))
        .join(' ');
      if (msg) report(new Error(msg), 'console.error');
    }
    originalError.apply(console, args);
  };

  // 4. console.warn — noise filtering only. This used to file a low-priority
  //    ticket for every warning matching 'failed' / 'timeout' / 'retry' etc,
  //    which was the largest single source of auto-generated reports. Gone.
  const originalWarn = console.warn;
  console.warn = (...args: unknown[]) => {
    if (
      isExtensionNoiseArgs(args) ||
      isBenignPdfNoiseArgs(args) ||
      isBenignBrowserNoiseArgs(args)
    ) {
      originalWarn.apply(console, args);
      return;
    }
    originalWarn.apply(console, args);
  };

  // 5. The fetch interceptor that filed a ticket for every 401/403/5xx coming
  //    back from our edge function has been removed. It wrapped globalThis.fetch
  //    for no purpose other than auto-reporting, and auto-reporting is off.
}

/** Kept so existing call sites keep compiling. It no longer files anything — it
 *  runs the same noise / stale-deploy classification as the global handlers, so
 *  a chunk-load failure routed through it still triggers the update recovery. */
export function reportError(error: unknown, source = 'manual') {
  if (error instanceof Error) {
    report(error, source);
  } else if (typeof error === 'string') {
    report(new Error(error), source);
  }
}
